<script setup lang="ts" generic="TType extends InputType = 'text'">
import {
  computed,
  type CSSProperties,
  inject,
  nextTick,
  onMounted,
  ref,
  useAttrs,
  useTemplateRef,
  watch,
} from "vue";
import {
  type ControlSize,
  type InputType,
  CONTROLS_DESIGN_VERSION,
  DEFAULT_CONTROLS_DESIGN_VERSION,
  DEFAULT_DESIGN_VERSION,
  DESIGN_VERSION,
} from "../models";

export type BcInputProps<TValue, TType extends InputType = "text"> = {
  /** Disables input element */
  disabled?: boolean;
  /** Specifies custom validation for the input */
  customValidation?: (value?: TValue) => boolean;
  /** Expands the input to the width of its parent container */
  fullWidth?: boolean;
  /** Specifies custom validation messages */
  errors?: Map<string, string>;
  /**
   * Specifies a label for the input.
   * The label is bound to the input.
   */
  label?: string | number | boolean;
  /** Specifies wether two-way binding should sync on change event instead of input */
  lazy?: boolean;
  /** Specifies wether a reveal icon should be shown for the password input */
  passwordToggle?: boolean;
  /** Expands the input to the width of the placeholder */
  placeholderWidth?: boolean;
  /** Specifies that input is read-only */
  readonly?: boolean;
  /** Specifies pre-defined sizes for the input */
  size?: ControlSize;
  /** Specifies the type for the input */
  type?: TType;
};

export type BcInputEmits<TEmit> = {
  (event: "blur", payload?: TEmit): void;
  (event: "change", payload: TEmit): void;
  (event: "focus"): void;
  (event: "input", payload: TEmit): void;
};

type TValue<T extends InputType> = T extends "number" ? number : string;

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();

/** Specifies two-way binding for the input */
const modelValue = defineModel<TValue<TType>>();

const {
  disabled = false,
  customValidation,
  fullWidth = false,
  errors = new Map<string, string>(),
  label = undefined,
  lazy = false,
  passwordToggle = false,
  placeholderWidth = false,
  readonly = false,
  size,
  type = "text" as TType,
} = defineProps<BcInputProps<TValue<TType>, TType>>();

const emit = defineEmits<BcInputEmits<TValue<TType>>>();

const dataType = ref<InputType>(type);
const errorKeys = ref<string[]>([]);
const isPasswordVisible = ref(false);
const validity = ref(true);

const inputRef = useTemplateRef("inputRef");

const design = inject(
  DESIGN_VERSION,
  computed(() => DEFAULT_DESIGN_VERSION),
);
const controlsDesign = inject(
  CONTROLS_DESIGN_VERSION,
  computed(() => DEFAULT_CONTROLS_DESIGN_VERSION),
);
const pak = computed(() => `bc-controls-d${controlsDesign.value}`);

const rootClass = computed(() => (attrs.class ? (attrs.class as string).split(" ") : []));
const rootStyle = computed(() => attrs.style as CSSProperties);
const inheritedAttrs = computed(() => {
  const { class: _, style: __, ...otherProps } = attrs;
  const inheritedProps = { ...otherProps };
  return inheritedProps;
});

onMounted(async () => {
  await nextTick();
  if (!disabled && inputRef.value?.value !== "") {
    checkValidity();
  }
  if (
    attrs.required &&
    inputRef.value &&
    (modelValue.value == null ||
      modelValue.value === "" ||
      inputRef.value.value == null ||
      inputRef.value.value === "")
  ) {
    inputRef.value.title = errors.get("valueMissing") ?? "";
  }
});

function checkValidity(): boolean {
  errorKeys.value = [];
  if (inputRef.value) {
    inputRef.value.title = "";
  }

  if (disabled || readonly || !inputRef.value) {
    validity.value = true;
    return validity.value;
  }

  if (customValidation) {
    const validationResult = customValidation(modelValue.value);
    if (typeof validationResult === "boolean") {
      if (!validationResult && errorKeys.value.length === 0) {
        errorKeys.value.push("invalidValue");
        if (!errors.has("invalidValue")) {
          errors.set("invalidValue", "invalidValue");
        }
      }
      validity.value = validationResult;
      return validity.value;
    } else {
      console.warn("Custom validation function is invalid.");
    }
  }

  inputRef.value.checkValidity();

  if (
    inputRef.value.validity.valueMissing ||
    (attrs.required && (!inputRef.value.value || inputRef.value.value === ""))
  ) {
    errorKeys.value.push("valueMissing");
    if (!errors.has("valueMissing")) {
      errors.set("valueMissing", "valueMissing");
    }
    inputRef.value.title = getValidationMessage("valueMissing");
    validity.value = false;
    return validity.value;
  }

  if (inputRef.value.validity.badInput) {
    errorKeys.value.push("badInput");
    if (!errors.has("badInput")) {
      errors.set("badInput", "badInput");
    }
    inputRef.value.title = getValidationMessage("badInput");
    validity.value = false;
    return validity.value;
  }

  if (
    type === "email" &&
    (inputRef.value.validity.typeMismatch ||
      !validatePattern(
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
      ))
  ) {
    errorKeys.value.push("emailMismatch");
    if (!errors.has("emailMismatch")) {
      errors.set("emailMismatch", "emailMismatch");
    }
    inputRef.value.title = getValidationMessage("emailMismatch");
    validity.value = false;
  } else if (inputRef.value.validity.typeMismatch) {
    // Chose error message depending on type
    errorKeys.value.push(`${type}Mismatch`);
    if (!errors.has(`${type}Mismatch`)) {
      errors.set(`${type}Mismatch`, `${type}Mismatch`);
    }
    inputRef.value.title = getValidationMessage(`${type}Mismatch`);
    validity.value = false;
  }

  // Check if input value matches the provided pattern
  if (!validatePattern()) {
    // Chose error message depending on type
    if (type === "password") {
      errorKeys.value.push("passwordMismatch");
      if (!errors.has("passwordMismatch")) {
        errors.set("passwordMismatch", "passwordMismatch");
      }
      inputRef.value.title = getValidationMessage("passwordMismatch");
    } else {
      errorKeys.value.push("patternMismatch");
      if (!errors.has("patternMismatch")) {
        errors.set("patternMismatch", "patternMismatch");
      }
      inputRef.value.title = getValidationMessage("patternMismatch");
    }
    validity.value = false;
  }

  if (inputRef.value.validity.tooShort) {
    errorKeys.value.push("tooShort");
    if (!errors.has("tooShort")) {
      errors.set("tooShort", "tooShort");
    }
    inputRef.value.title = getValidationMessage("tooShort");
    validity.value = false;
  }

  if (inputRef.value.validity.tooLong) {
    errorKeys.value.push("tooLong");
    if (!errors.has("tooLong")) {
      errors.set("tooLong", "tooLong");
    }
    inputRef.value.title = getValidationMessage("tooLong");
    validity.value = false;
  }

  if (inputRef.value.validity.rangeUnderflow) {
    errorKeys.value.push("rangeUnderflow");
    if (!errors.has("rangeUnderflow")) {
      errors.set("rangeUnderflow", "rangeUnderflow");
    }
    inputRef.value.title = getValidationMessage("rangeUnderflow");
    validity.value = false;
  }

  if (inputRef.value.validity.rangeOverflow) {
    errorKeys.value.push("rangeOverflow");
    if (!errors.has("rangeOverflow")) {
      errors.set("rangeOverflow", "rangeOverflow");
    }
    inputRef.value.title = getValidationMessage("tooLong");
    validity.value = false;
  }

  if (!inputRef.value.validity) {
    errorKeys.value.push(inputRef.value.validationMessage);
    validity.value = false;
  } else if (!errorKeys.value.length) {
    inputRef.value.title = "";
    validity.value = true;
  }

  return validity.value;
}

function getValidationMessage(errorKey: string): string {
  return errors.get(errorKey) ?? errorKey;
}

function onBlur(): void {
  checkValidity();
  emit("blur", modelValue.value);
}

async function onChange(): Promise<void> {
  const rawValue = inputRef.value?.value;
  if (rawValue == null) return;
  const typedValue = parseValue<TType>(type ?? "text", rawValue);
  modelValue.value = typedValue;
  lazy ? emit("input", typedValue) : emit("change", typedValue);
  await nextTick();
  checkValidity();
}

async function onInput(): Promise<void> {
  if (lazy) return;
  const rawValue = inputRef.value?.value;
  if (rawValue == null) return;
  const typedValue = parseValue<TType>(type ?? "text", rawValue);
  modelValue.value = typedValue;
  emit("input", typedValue);
  await nextTick();
  checkValidity();
}

function parseValue<T extends InputType>(type: T, value: string): TValue<T> {
  if (type === "number") {
    return (value === "" ? "" : Number(value)) as TValue<T>;
  }
  return value as TValue<T>;
}

function onKeypress(event: KeyboardEvent): void {
  if (type === "number") {
    const min =
      typeof attrs.min === "string" && !Number.isNaN(Number.parseFloat(attrs.min))
        ? Number.parseFloat(attrs.min)
        : null;
    if (event.key === "-") {
      if ((event.target as HTMLInputElement).value !== "" || (min !== null && min >= 0))
        event.preventDefault();
    } else if (event.key === ".") {
      if ((event.target as HTMLInputElement).value.includes(".")) event.preventDefault();
    } else if (Number.isNaN(Number.parseFloat(event.key))) {
      event.preventDefault();
    }
  }
}

function togglePasswordVisibility(): void {
  if (!disabled) {
    isPasswordVisible.value = !isPasswordVisible.value;
    dataType.value = isPasswordVisible.value ? "text" : "password";
  }

  nextTick(() => {
    inputRef.value?.focus();
  });
}

function validatePattern(pattern?: RegExp | string): boolean {
  if (pattern && inputRef.value) {
    return new RegExp(pattern).test(inputRef.value.value);
  }
  return inputRef.value &&
    attrs.pattern &&
    (typeof attrs.pattern === "string" || attrs.pattern instanceof RegExp)
    ? new RegExp(attrs.pattern).test(inputRef.value.value)
    : true;
}

function onErrorsChanged(): void {
  if (inputRef.value && errorKeys.value.length) {
    inputRef.value.title = errors.get(errorKeys.value[0]) ?? "";
  }
}

function onDisabledOrReadonlyChanged(value: boolean): void {
  if (inputRef.value) {
    inputRef.value.title =
      value && errorKeys.value.length ? "" : (errors.get(errorKeys.value[0]) ?? "");
  }
}

function onTypeChanged(type: InputType): void {
  dataType.value = type;
}

watch(() => type, onTypeChanged);
watch(() => errors, onErrorsChanged, { deep: true });
watch(() => disabled, onDisabledOrReadonlyChanged);
watch(() => readonly, onDisabledOrReadonlyChanged);

defineExpose({
  attrs,
  modelValue,
  dataType,
  errorKeys,
  isPasswordVisible,
  validity,
  inputRef,
  checkValidity,
  getValidationMessage,
  togglePasswordVisibility,
  validatePattern,
});
</script>

<template>
  <label
    v-bind:data-pak="pak"
    v-bind:data-design="design"
    class="bissantzControls inputControl"
    v-bind:class="[
      size ? `input-${size}` : '',
      {
        disabled: disabled,
        fullWidth: fullWidth,
        invalid: !validity,
        passwordToggle: passwordToggle,
        readonly: readonly,
      },
      ...rootClass,
    ]"
    v-bind:style="rootStyle"
  >
    <span v-if="label !== undefined" class="label">{{ label }}</span>
    <span v-if="placeholderWidth" class="widthtestbox">{{ $attrs.placeholder }}</span>
    <div class="inputContainer">
      <input
        ref="inputRef"
        v-bind="inheritedAttrs"
        v-bind:type="dataType"
        v-bind:value="modelValue"
        v-bind:disabled="disabled"
        v-bind:readonly="readonly"
        v-on:input="onInput"
        v-on:change="onChange"
        v-on:blur="onBlur"
        v-on:focus="emit('focus')"
        v-on:keypress="onKeypress"
      />
      <template v-if="type === 'password' && passwordToggle">
        <template v-if="!isPasswordVisible">
          <span class="visibility" v-on:click="togglePasswordVisibility">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 48 48"
              width="1.25em"
              height="1.5em"
            >
              <path
                d="M24 31.5q3.55 0 6.025-2.475Q32.5 26.55 32.5 23q0-3.55-2.475-6.025Q27.55 14.5 24 14.5q-3.55 0-6.025 2.475Q15.5 19.45 15.5 23q0 3.55 2.475 6.025Q20.45 31.5 24 31.5Zm0-2.9q-2.35 0-3.975-1.625T18.4 23q0-2.35 1.625-3.975T24 17.4q2.35 0 3.975 1.625T29.6 23q0 2.35-1.625 3.975T24 28.6Zm0 9.4q-7.3 0-13.2-4.15Q4.9 29.7 2 23q2.9-6.7 8.8-10.85Q16.7 8 24 8q7.3 0 13.2 4.15Q43.1 16.3 46 23q-2.9 6.7-8.8 10.85Q31.3 38 24 38Zm0-15Zm0 12q6.05 0 11.125-3.275T42.85 23q-2.65-5.45-7.725-8.725Q30.05 11 24 11t-11.125 3.275Q7.8 17.55 5.1 23q2.7 5.45 7.775 8.725Q17.95 35 24 35Z"
              />
            </svg>
          </span>
        </template>
        <template v-else>
          <span class="visibilityOff" v-on:click="togglePasswordVisibility">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 48 48"
              width="1.25em"
              height="1.5em"
            >
              <path
                d="m31.45 27.05-2.2-2.2q1.3-3.55-1.35-5.9-2.65-2.35-5.75-1.2l-2.2-2.2q.85-.55 1.9-.8 1.05-.25 2.15-.25 3.55 0 6.025 2.475Q32.5 19.45 32.5 23q0 1.1-.275 2.175-.275 1.075-.775 1.875Zm6.45 6.45-2-2q2.45-1.8 4.275-4.025Q42 25.25 42.85 23q-2.5-5.55-7.5-8.775Q30.35 11 24.5 11q-2.1 0-4.3.4-2.2.4-3.45.95L14.45 10q1.75-.8 4.475-1.4Q21.65 8 24.25 8q7.15 0 13.075 4.075Q43.25 16.15 46 23q-1.3 3.2-3.35 5.85-2.05 2.65-4.75 4.65Zm2.9 11.3-8.4-8.25q-1.75.7-3.95 1.075T24 38q-7.3 0-13.25-4.075T2 23q1-2.6 2.775-5.075T9.1 13.2L2.8 6.9l2.1-2.15L42.75 42.6ZM11.15 15.3q-1.85 1.35-3.575 3.55Q5.85 21.05 5.1 23q2.55 5.55 7.675 8.775Q17.9 35 24.4 35q1.65 0 3.25-.2t2.4-.6l-3.2-3.2q-.55.25-1.35.375T24 31.5q-3.5 0-6-2.45T15.5 23q0-.75.125-1.5T16 20.15Zm15.25 7.1Zm-5.8 2.9Z"
              />
            </svg>
          </span>
        </template>
      </template>
    </div>
    <div
      v-show="!disabled && !readonly && !validity && errorKeys.length"
      class="validationMessage"
    >
      <span
        v-for="(errorKey, idx) in errorKeys"
        v-bind:key="idx"
        v-bind:data-locid="errorKey"
        >{{ getValidationMessage(errorKey) }}</span
      >
    </div>
  </label>
</template>

<style lang="less">
@import "../styles/components/bc-input.less";
</style>
